/**
 * Analytics API Service
 *
 * Set VITE_DEMO_MODE=true in .env.local to use fake data for recordings.
 * Remove (or set to false) to restore live platform data.
 */
import apiClient from './api';
import type {
  DashboardMetrics,
  CampaignSummary,
  ChartData,
  ChartMetric,
  ActivityItem,
  Highlight,
  SyncStatus,
  TimeRange,
  Platform,
} from '@/types/analytics';
import {
  mockGetDashboard,
  mockGetCampaignsSummary,
  mockGetChartData,
  mockGetHighlights,
  mockGetRecentActivity,
  mockGetSyncStatus,
} from './analyticsApiMock';

const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === 'true';

export const analyticsApi = {
  /**
   * Totals for the period, change vs the previous period, and per-platform numbers
   */
  async getDashboard(timeRange: TimeRange = '30d', platform: Platform = 'all'): Promise<DashboardMetrics> {
    if (DEMO_MODE) return mockGetDashboard(timeRange, platform);
    const response = await apiClient.get('/api/v1/analytics/dashboard', {
      params: { time_range: timeRange, platform }
    });
    return response.data;
  },

  /**
   * Campaigns that delivered in the period, with goal-based results
   */
  async getCampaignsSummary(params?: {
    time_range?: TimeRange;
    platform?: string;
    status?: string;
    limit?: number;
    offset?: number;
    sort_by?: 'spend' | 'results' | 'cost_per_result' | 'ctr' | 'impressions' | 'clicks';
    sort_order?: 'asc' | 'desc';
  }): Promise<{ campaigns: CampaignSummary[]; total: number; limit: number; offset: number }> {
    if (DEMO_MODE) return mockGetCampaignsSummary(params?.platform);
    const response = await apiClient.get('/api/v1/analytics/campaigns/summary', { params });
    return response.data;
  },

  /**
   * One point per day for the period, split by platform
   */
  async getChartData(
    metric: ChartMetric,
    timeRange: TimeRange = '30d',
    platform: Platform = 'all',
  ): Promise<ChartData> {
    if (DEMO_MODE) return mockGetChartData(metric, timeRange, platform);
    const response = await apiClient.get('/api/v1/analytics/chart', {
      params: { metric, time_range: timeRange, platform, group_by: 'day' }
    });
    return response.data;
  },

  /**
   * Connection health and last sync per connected platform
   */
  async getSyncStatus(): Promise<SyncStatus> {
    if (DEMO_MODE) return mockGetSyncStatus();
    const response = await apiClient.get('/api/v1/analytics/sync-status');
    return response.data;
  },

  /**
   * What is working and what needs attention in the period
   */
  async getHighlights(timeRange: TimeRange = '30d', platform: Platform = 'all'): Promise<{ highlights: Highlight[] }> {
    if (DEMO_MODE) return mockGetHighlights(platform);
    const response = await apiClient.get('/api/v1/analytics/highlights', {
      params: { time_range: timeRange, platform }
    });
    return response.data;
  },

  /**
   * Get campaign detail
   */
  async getCampaignDetail(campaignId: string, timeRange: TimeRange = '30d') {
    if (DEMO_MODE) return null; // detail view not needed for dashboard demo
    const response = await apiClient.get(`/api/v1/analytics/campaigns/${campaignId}`, {
      params: { time_range: timeRange }
    });
    return response.data;
  },

  /**
   * Get recent activity
   */
  async getRecentActivity(limit: number = 10): Promise<{ activities: ActivityItem[]; unread_count: number }> {
    if (DEMO_MODE) return mockGetRecentActivity(limit);
    const response = await apiClient.get('/api/v1/analytics/activity/recent', {
      params: { limit }
    });
    return response.data;
  }
};
